"use client";

import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { TaskStatus } from "@/types";
import { QuestCard } from "@/app/quests/_components/quest-card";
import { QuestModal } from "@/app/quests/_components/quest-modal";
import {
  useGetCampaign,
  useGetCampaignTaskStatus,
  useGetCampaignUser,
} from "@/hooks/challenges";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";
import { UserStats } from "./user-stats";
import { useAuth } from "@/hooks/useAuth";
import { groupAndFilterByActiveTasks } from "@/lib/challenges";
import { QuestsPageSkeleton } from "./quests-page-skeleton";

export function QuestsPage() {
  const [selectedTask, setSelectedTask] = useState<TaskStatus | null>(null);
  const searchParams = useSearchParams();
  const router = useRouter();
  const { address, isAuthenticated: isDynamicAuth, login } = useAuth();

  const { data: campaign, isLoading: isCampaignLoading } = useGetCampaign(
    CAMPAIGN_ID,
    CAMPAIGN_GROUP_ID
  );
  const { data: taskStatuses, isLoading: isTasksLoading } =
    useGetCampaignTaskStatus(CAMPAIGN_ID, CAMPAIGN_GROUP_ID);
  const { data: challengesUser, isLoading: isChallengesUserLoading } =
    useGetCampaignUser(
      CAMPAIGN_ID,
      CAMPAIGN_GROUP_ID,
      address || "",
      isDynamicAuth
    );

  const tasks = useMemo(() => {
    const tasksStatus = taskStatuses?.tasksStatus || [];
    return tasksStatus;
  }, [taskStatuses]);

  // Calculate daily streak from recurring_task
  const dailyStreak = useMemo(() => {
    const recurringTask = tasks.find(
      (task: TaskStatus) => task.taskName === "recurring_task"
    );
    if (recurringTask?.extraArguments?.[0]) {
      try {
        const streakData = JSON.parse(recurringTask.extraArguments[0]);
        return streakData?.currentStreak || 0;
      } catch (e) {
        return 0;
      }
    }
    return 0;
  }, [tasks]);

  // Calculate completed and total tasks
  const { completedTasks, totalTasks } = useMemo(() => {
    const completed = tasks.filter(
      (task: TaskStatus) => task.isEnabled && task.status === "SUCCESSFUL"
    ).length;
    const total = tasks.filter((task: TaskStatus) => task.isEnabled).length;
    return { completedTasks: completed, totalTasks: total };
  }, [tasks]);

  // Handle taskGuid query parameter - only once when data is loaded
  useEffect(() => {
    const taskGuid = searchParams.get("taskGuid");
    if (taskGuid && tasks.length > 0 && !isTasksLoading) {
      const task = tasks.find((t: TaskStatus) => t.taskGuid === taskGuid);
      if (task) {
        setSelectedTask(task);
      }
    }
  }, [searchParams, tasks, isTasksLoading]);

  // Handle opening a task and updating URL
  const handleOpenTask = (task: TaskStatus) => {
    setSelectedTask(task);
    const params = new URLSearchParams(searchParams.toString());
    params.set("taskGuid", task.taskGuid);
    router.replace(`/quests?${params.toString()}`, { scroll: false });
  };

  // Handle modal close to remove query param
  const handleCloseModal = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("taskGuid");

    setSelectedTask(null);
    // Remove taskGuid from URL
    const newUrl = params.toString()
      ? `/quests?${params.toString()}`
      : "/quests";
    router.replace(newUrl, { scroll: false });
  };

  // Group tasks by type
  const taskGroups = useMemo(() => {
    if (!campaign) return [];
    return groupAndFilterByActiveTasks(campaign, tasks);
  }, [campaign, tasks]);

  // Task type names in order
  const taskTypeOrder = [
    "On-Chain Challenges",
    "Periodic Challenges",
    "Reactor Quests",
    "Creation Challenges",
    "Game Challenges",
    "Special Challenges",
    "Quiz Challenges",
    "Engage Challenges",
    "Game of Life Challenges",
  ];

  // Show loading skeleton while data is being fetched
  console.log({
    isCampaignLoading,
    isTasksLoading,
    isChallengesUserLoading,
    tasksLength: tasks.length,
  });
  if (isCampaignLoading || isTasksLoading || !tasks?.length) {
    return <QuestsPageSkeleton />;
  }

  return (
    <div className="min-h-screen">
      {/* User Stats */}
      <UserStats
        address={address || undefined}
        name={challengesUser?.name}
        totalPoints={Number(challengesUser?.totalPoints || 0)}
        leaderboardRank={challengesUser?.rank || 0}
        dailyStreak={dailyStreak}
        completedTasks={completedTasks}
        totalTasks={totalTasks}
      />

      {/* Quests Section */}
      <div className="w-full max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-12">
        {taskGroups.map((taskGroup, groupIndex) => {
          // Get the task type name for this group
          const firstTask = taskGroup[0];
          const taskTypeName =
            firstTask?.type || taskTypeOrder[groupIndex] || "Challenges";

          return (
            <div key={groupIndex} className="mb-12 last:mb-0">
              {/* Section Header */}
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                {taskTypeName}
              </h3>

              {/* Quest Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8">
                {taskGroup.map((task: TaskStatus) => {
                  const challengeTask = campaign?.tasks?.find(
                    (t: any) => t.id === task.taskGuid
                  );

                  return (
                    <QuestCard
                      key={task.taskGuid}
                      quest={task}
                      challengeTask={challengeTask}
                      onClick={() => handleOpenTask(task)}
                      isLocked={false}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quest Modal */}
      <QuestModal
        isOpen={!!selectedTask}
        onClose={handleCloseModal}
        task={selectedTask}
      />
    </div>
  );
}
